import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import { JsonLd, breadcrumbSchema } from '../components/JsonLd';
import { AREAS, areaLabels } from '../config/areas';
import { pageCopy } from '../config/pageCopy';
import { LANGS, isRtl as rtl } from '../config/langs';
import { cityName, ui, projectCount } from '../config/seoCopy';
import { useBuildData } from '../utils/buildData';

/**
 * /areas — the hub between the homepage and the area pages, so each area is
 * two clicks from the root rather than reachable only through the sitemap.
 * Lists every area the build published (/data/areas.json), curated first.
 */
export default function AreasPage() {
    const { i18n } = useTranslation();
    const base = (i18n.language || 'ar').split('-')[0];
    const lang = LANGS.includes(base) ? base : 'en';
    const copy = pageCopy('areas', lang) || pageCopy('areas', 'en');
    const L = areaLabels(lang);
    const U = ui(lang);
    const { data } = useBuildData('areas.json');
    // Until the list arrives, the curated areas from the bundle.
    const areas = data || AREAS.map((a) => ({ ...a, liveCount: 0 }));
    const cities = [...new Set(areas.map((a) => a.city))];

    return (
        <div style={{ minHeight: '100vh' }} dir={rtl(lang) ? 'rtl' : 'ltr'}>
            <SEO fullTitle={copy.title} description={copy.description} path="/areas" />
            <JsonLd data={breadcrumbSchema([
                { name: U.home, url: '/' },
                { name: U.areas, url: '/areas' }
            ])} />
            <Header />

            <main className="container" style={{ padding: '140px 1rem 5rem' }}>
                <Breadcrumbs items={[{ name: U.home, to: '/' }, { name: U.areas }]} />
                <h1 style={{ fontSize: 'clamp(2rem, 5vw, 3rem)', fontWeight: 800, color: '#fff', marginBottom: '1rem' }}>
                    {copy.h1}
                </h1>
                <p style={{ color: '#aaa', lineHeight: 1.9, maxWidth: 860, marginBottom: '2.5rem' }}>
                    {copy.answer()}
                </p>

                {cities.map((city) => (
                    <section key={city} style={{ marginBottom: '2.5rem' }}>
                        <h2 style={{ color: 'var(--color-gold)', fontSize: '1.4rem', marginBottom: '1rem' }}>
                            {cityName(city, lang)}
                        </h2>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '1rem' }}>
                            {areas.filter((a) => a.city === city).map((a) => (
                                <Link
                                    key={a.slug}
                                    to={`/area/${a.slug}`}
                                    style={{
                                        border: '1px solid rgba(255,255,255,.1)', borderRadius: 12, padding: '1rem 1.25rem',
                                        textDecoration: 'none', color: '#fff', background: 'rgba(255,255,255,.02)'
                                    }}
                                >
                                    <strong style={{ display: 'block', marginBottom: '.35rem' }}>{L.link(a)}</strong>
                                    {a.liveCount > 0 && <span style={{ color: '#999', fontSize: '.9rem' }}>{projectCount(a.liveCount, lang)}</span>}
                                </Link>
                            ))}
                        </div>
                    </section>
                ))}
            </main>

            <Footer />
        </div>
    );
}
